import React, { useCallback, useEffect, useRef, useState } from 'react';

import { formatUtcToLocalTimeString } from 'utils/dateUtils/ledgerDateUtils';

import { useLedgerApi } from '../../useLedgerApi';

import { InventoryEventCard } from './InventoryEventCard';

import '../../inventoryHistoryV2.css';

const TRANSFER_DISCREPANCY_TYPE_LABELS = {
    expiration_date_discrepancy: 'Expiration date discrepancy',
    total_unit_discrepancy: 'Total unit discrepancy',
    units_per_case_discrepancy: 'Units per case discrepancy',
};

const TRANSFER_DISCREPANCY_FIELD_LABELS = {
    expectedUnitsPerCase: 'Expected units per case',
    receivedUnitsPerCase: 'Received units per case',
    expectedNumCases: 'Expected number of cases',
    receivedNumCases: 'Received number of cases',
    expectedNumLooseUnits: 'Expected loose units',
    receivedNumLooseUnits: 'Received loose units',
    expectedTotalUnits: 'Expected total units',
    receivedTotalUnits: 'Received total units',
    expectedExpiresOn: 'Expected expires on',
    receivedExpiresOn: 'Received expires on',
};

const TRANSFER_DISCREPANCY_DETAIL_KEY_ORDER = Object.keys(TRANSFER_DISCREPANCY_FIELD_LABELS);

function formatTransferDiscrepancyType(type) {
    if (!type || typeof type !== 'string') {
        return '';
    }
    return (
        TRANSFER_DISCREPANCY_TYPE_LABELS[type] ??
        type
            .split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(' ')
    );
}

function hasTransferDiscrepancyDetailValue(value) {
    if (value === null || value === undefined) {
        return false;
    }
    if (typeof value === 'string' && value.trim() === '') {
        return false;
    }
    return true;
}

function getTransferDiscrepancyNotes(event) {
    if (event.eventType !== 'Transfer Discrepancy' || !event.transferDiscrepancyDetails) {
        return '';
    }

    const details = event.transferDiscrepancyDetails;
    const lines = [];

    for (const key of TRANSFER_DISCREPANCY_DETAIL_KEY_ORDER) {
        if (Object.prototype.hasOwnProperty.call(details, key) && hasTransferDiscrepancyDetailValue(details[key])) {
            lines.push(`${TRANSFER_DISCREPANCY_FIELD_LABELS[key] ?? key}: ${details[key]}`);
        }
    }

    const knownDetailKeys = new Set(['discrepancyType', 'transferId', ...TRANSFER_DISCREPANCY_DETAIL_KEY_ORDER]);
    for (const key of Object.keys(details).sort()) {
        if (!knownDetailKeys.has(key) && hasTransferDiscrepancyDetailValue(details[key])) {
            const label = key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^\s+/, '')
                .replace(/^\w/, (c) => c.toUpperCase());
            lines.push(`${label || key}: ${details[key]}`);
        }
    }

    return lines.join('\n');
}

/**
 * Expanded card showing event history for a SKU or Lot
 *
 * @param {string} type - The type of event history to fetch ('sku' or 'lot')
 * @param {string} skuOrLotId - The SKU or lot ID to fetch event history for
 * @param {boolean} loading - Whether the initial loading is happening
 * @param {array} expandedFilters - Array of filters for event history
 * @param {function} setNotification - Function to set notifications
 * @param {function} filtersToApiParams - Function to convert filters to API parameters
 * @param {string} facilityId - The facility ID
 * @param {boolean} summarizePackout - Whether to summarize packout events
 */
function EventsExpandedContainer({
    companySourceId,
    displaySource = false,
    expandedFilters = [],
    facilityId,
    filtersToApiParams,
    loading = false,
    setNotification,
    skuOrLotId,
    summarizePackout = false,
    type,
}) {
    const ledgerApi = useLedgerApi();
    const [isLoading, setIsLoading] = useState(loading);
    const [eventHistory, setEventHistory] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const observerRef = useRef(null);
    const loaderRef = useRef(null);

    const fetchEventHistory = async (loadMore = false) => {
        if (!loadMore) {
            setIsLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        const dateRangeFilter = expandedFilters.find((filter) => filter.filterKey === 'dateRange');
        if (!dateRangeFilter) {
            setEventHistory([]);
            setNotification({
                text: 'Date range filter is required to view event history',
                level: 'warning',
            });
            setIsLoading(false);
            return;
        }

        let queryString = filtersToApiParams(expandedFilters, summarizePackout);

        // Strip out any sku or lot filters from the query string
        // Remove sku[]=XXX and lot[]=XXX from the query string if they exist
        queryString = queryString.replace(/[&?]sku\[\]=[^&]*/g, '');
        queryString = queryString.replace(/[&?]lot\[\]=[^&]*/g, '');
        // Fix the query string if it starts with '&' after removal
        if (queryString.startsWith('&')) {
            queryString = `?${queryString.substring(1)}`;
        }

        const response = await ledgerApi.getEventHistory(
            type,
            skuOrLotId,
            companySourceId,
            facilityId,
            queryString,
            page,
            50,
        );

        if (response.status && response.data.rows) {
            const events = response.data.rows.map((event) => ({
                timestamp: event.timestamp,
                eventType: event.eventType,
                location: event.warehouseLocationName,
                fromLocation: event.originWarehouseLocationName,
                toLocation: event.destinationWarehouseLocationName,
                lotNumber: event.lotId,
                sku: event.sku,
                quantity: event.quantityAdjusted ?? event.numberOfUnits,
                runningTotal: event.runningTotalUnits,
                user: event.authUser,
                notes: event.message,
                shipmentId: event.shipmentId,
                source: event.source,
                adjustmentReason: event.adjustmentReason,
                qcStatus: event.status,
                expectedCaseCount: event.expectedCaseCount,
                actualCaseCount: event.actualCaseCount,
                expectedLooseUnitCount: event.expectedLooseUnitCount,
                actualLooseUnitCount: event.actualLooseUnitCount,
                cycleCountIssues: event.cycleCountIssues,
                discrepancy: event.discrepancy,
                oldExpirationDate: event.oldExpirationDate,
                newExpirationDate: event.newExpirationDate,
                reasonCode: event.reason ?? '',
                palletId: event.palletId ?? '',
                transferDiscrepancyDetails: event.transferDiscrepancyDetails,
            }));

            setEventHistory((prev) => (loadMore ? [...prev, ...events] : events));

            const shouldHaveMore = events.length === 50;
            setHasMore(shouldHaveMore);
        } else {
            if (!loadMore) {
                setEventHistory([]);
            }
            setNotification({
                text: response.msg ?? 'Error fetching event history',
                level: 'error',
            });
            setHasMore(false);
        }

        if (loadMore) {
            setIsLoadingMore(false);
        } else {
            setIsLoading(false);
        }
    };

    // Intersection Observer callback for infinite scroll
    const handleObserver = useCallback(
        (entries) => {
            const target = entries[0];
            if (target.isIntersecting && !isLoading && !isLoadingMore && hasMore) {
                setPage((prev) => prev + 1);
            }
        },
        [isLoading, isLoadingMore, hasMore, page],
    );

    // Setup Intersection Observer
    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '20px',
            threshold: 0,
        };

        observerRef.current = new IntersectionObserver(handleObserver, option);

        // Try to attach observer if loader element exists
        if (loaderRef.current && hasMore) {
            observerRef.current.observe(loaderRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [handleObserver, hasMore]);

    // Attach observer when loader element becomes available
    useEffect(() => {
        // Clean up previous observation
        if (observerRef.current && loaderRef.current) {
            observerRef.current.unobserve(loaderRef.current);
        }

        // Attach observer if both loader and observer exist
        if (loaderRef.current && observerRef.current && hasMore) {
            observerRef.current.observe(loaderRef.current);
        }

        // Cleanup function
        return () => {
            if (observerRef.current && loaderRef.current) {
                observerRef.current.unobserve(loaderRef.current);
            }
        };
    }, [eventHistory, hasMore]);

    // Fetch data when component mounts or when filters/skuOrLotId change
    useEffect(() => {
        setPage(0);
        setHasMore(true);
        fetchEventHistory(false);
    }, [skuOrLotId, JSON.stringify(expandedFilters), summarizePackout, companySourceId]);

    // Effect to handle pagination
    useEffect(() => {
        if (page > 0) {
            fetchEventHistory(true);
        }
    }, [page]);

    const formatDateTime = (timestamp) => formatUtcToLocalTimeString(timestamp);

    const shouldShowAsChangeValue = (eventType) => {
        const nonChangeEvents = [
            'Moved Event',
            'QC Hold Status Change',
            'Expiration Date Changed',
            'Transfer Discrepancy',
        ];
        return !nonChangeEvents.includes(eventType);
    };

    const getEventNotes = (event) => {
        if (!event) {
            return '';
        }

        if (event.notes) {
            return event.notes;
        }

        switch (event.eventType) {
            case 'Moved Event':
                return `Moved ${Math.abs(event.quantity || 0)} units from ${event.fromLocation || ''} to ${
                    event.toLocation || ''
                }`;
            case 'Inventory Adjusted Event':
                return `${(event.quantity || 0) > 0 ? 'Increased' : 'Decreased'} inventory by ${Math.abs(
                    event.quantity || 0,
                )} units${event.adjustmentReason ? ` due to ${event.adjustmentReason}` : ''}`;
            case 'Packout Event':
                return `Packed ${Math.abs(event.quantity || 0)} units for shipment${
                    event.shipmentId ? ` ${event.shipmentId}` : ''
                }`;
            case 'Received Event':
                return `Received ${event.quantity || 0} units${event.source ? ` from ${event.source}` : ''}`;
            case 'QC Hold Status Change':
                return `${event.qcStatus === 'QC_AUDIT' ? 'Placed on' : 'Released from'} QC hold`;
            case 'Expiration Date Changed':
                return `Expiration date changed from ${event.oldExpirationDate || ''} to ${
                    event.newExpirationDate || ''
                }`;
            case 'Cycle Count Event': {
                const mainCycleCountInfo = `Expected: ${event.expectedCaseCount || 0} case(s) ${
                    event.expectedLooseUnitCount || 0
                } loose unit(s)\nActual: ${event.actualCaseCount || 0} case(s) ${
                    event.actualLooseUnitCount || 0
                } loose unit(s)`;

                if (event.cycleCountIssues && event.cycleCountIssues.length > 0) {
                    const issuesInfo = `Issues: ${event.cycleCountIssues
                        .map((issue) => `${issue.issue_type}${issue.details ? ` (${issue.details})` : ''}`)
                        .join(', ')}`;
                    return `${mainCycleCountInfo}\n${issuesInfo}`;
                }

                return mainCycleCountInfo;
            }
            case 'Transfer Discrepancy':
                return getTransferDiscrepancyNotes(event);
            default:
                return '';
        }
    };

    const renderEventHistoryTable = () => (
        <div className="w-full">
            {eventHistory.length > 0 ? (
                <>
                    {eventHistory.map((event, index) => {
                        const baseInformation = [
                            {
                                key: 'Location',
                                value:
                                    event.eventType === 'Moved Event'
                                        ? `${event.fromLocation || ''} → ${event.toLocation || ''}`
                                        : event.location || '',
                            },
                            {
                                key: type === 'lot' ? 'SKU' : 'Lot Number',
                                value: type === 'lot' ? event.sku || '' : event.lotNumber || '',
                            },
                            {
                                key: 'Quantity',
                                value: {
                                    type: 'numberDisplay',
                                    value: event.quantity || 0,
                                    isChangeValue: shouldShowAsChangeValue(event.eventType || ''),
                                },
                            },
                            {
                                key: 'Running Total',
                                value: {
                                    type: 'numberDisplay',
                                    value: event.runningTotal || 0,
                                    isChangeValue: false,
                                },
                            },
                            { key: 'User', value: event.user || 'System' },
                            { key: 'Notes', value: getEventNotes(event) || '' },
                            { key: 'Reason Code', value: event.reasonCode || '' },
                            {
                                key: 'Pallet',
                                value: ['Prepped Event', 'Inventory Adjusted Event'].includes(event.eventType)
                                    ? event.palletId || ''
                                    : '',
                            },
                            {
                                key: 'Discrepancy Type',
                                value:
                                    event.eventType === 'Transfer Discrepancy' &&
                                    event.transferDiscrepancyDetails?.discrepancyType
                                        ? formatTransferDiscrepancyType(
                                              event.transferDiscrepancyDetails.discrepancyType,
                                          )
                                        : '',
                            },
                            {
                                key: 'Transfer ID',
                                value:
                                    event.eventType === 'Transfer Discrepancy' &&
                                    event.transferDiscrepancyDetails?.transferId != null
                                        ? String(event.transferDiscrepancyDetails.transferId)
                                        : '',
                            },
                            {
                                key: 'Company Source',
                                value: event.source || '',
                            },
                        ];

                        return (
                            <InventoryEventCard
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${event.lotNumber || event.sku || ''}-${index}`}
                                displaySource={displaySource}
                                eventType={event.eventType}
                                timestamp={formatDateTime(event.timestamp)}
                                baseInformation={baseInformation}
                            />
                        );
                    })}

                    {/* Infinite scroll loader element */}
                    {hasMore && (
                        <div
                            ref={loaderRef}
                            className="flex justify-center items-center py-4"
                            style={{ minHeight: '50px' }}
                        >
                            {isLoadingMore && <div className="spinner" />}
                        </div>
                    )}
                </>
            ) : (
                <div className="py-4 text-center number-empty">
                    {expandedFilters.length > 0 ? 'No events match the selected filters' : 'No event history available'}
                </div>
            )}
        </div>
    );

    return (
        <div className="w-full rounded-b bg-white">
            {isLoading ? (
                <div className="flex justify-center py-8">
                    <div className="w-8 h-8 spinner animate-spin" />
                </div>
            ) : (
                <div className="max-h-[600px] overflow-y-auto w-full p-0">{renderEventHistoryTable()}</div>
            )}
        </div>
    );
}

export { EventsExpandedContainer };
